import React from "react";
import { TID } from "@/constants/testIds";
import { COPY } from "@/copy";

const intentColor = (intent) => {
  switch ((intent || "").toLowerCase()) {
    case "hookup": return "bg-ds-flame text-ds-ink";
    case "situationship": return "bg-ds-yellow text-ds-ink";
    case "serious": return "bg-emerald-400 text-ds-ink";
    case "avoidance": return "bg-zinc-500 text-ds-bone";
    case "validation": return "bg-fuchsia-400 text-ds-ink";
    default: return "bg-ds-bone text-ds-ink";
  }
};

export const OutputCard = ({ result, loading }) => {
  if (loading) {
    return (
      <div className="ds-border ds-shadow-lg bg-ds-oxblood text-ds-bone p-8 min-h-[560px] flex flex-col justify-center items-center">
        <div className="ds-mono text-xs uppercase tracking-[0.4em] text-ds-flame animate-flicker mb-4">
          // DECODING
        </div>
        <div className="ds-display text-3xl text-center max-w-md leading-tight">
          reading between<br/>the lines<span className="text-ds-flame">_</span>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="ds-border bg-ds-paper p-8 min-h-[560px] flex flex-col justify-center items-center text-center">
        <div className="ds-mono text-xs uppercase tracking-[0.4em] text-ds-ink/40 mb-4">
          // OUTPUT
        </div>
        <div className="ds-display text-3xl max-w-sm leading-tight text-ds-ink/30">
          your decoded<br/>truth lands here.
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={TID.output}
      className="ds-border ds-shadow-lg bg-ds-oxblood text-ds-bone p-7 md:p-9 animate-reveal"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="ds-mono text-xs uppercase tracking-[0.4em] text-ds-flame">
          {COPY.output_kicker}
        </div>
        <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-bone/50">
          conf. {result.confidence}%
        </div>
      </div>

      {result.original_message && result.original_message !== "(decoded from screenshot)" && (
        <div className="mb-5 pb-5 border-b border-ds-bone/20">
          <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-bone/40 mb-1.5">they said</div>
          <div className="ds-display-medium text-lg text-ds-bone/80 leading-snug italic">
            &quot;{result.original_message}&quot;
          </div>
        </div>
      )}

      <div className="mb-5">
        <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-bone/40 mb-1.5">
          {COPY.output_meaning}
        </div>
        <div
          data-testid={TID.outputMeaning}
          className="ds-display text-2xl md:text-3xl leading-tight text-ds-bone"
        >
          {result.real_meaning}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <span
          data-testid={TID.outputIntent}
          className={`ds-mono text-[10px] uppercase tracking-widest px-2.5 py-1 ds-border border-ds-bone ${intentColor(result.intent)}`}
        >
          {COPY.output_intent}: {result.intent}
        </span>
        {result.vibe && (
          <span className="ds-mono text-[10px] uppercase tracking-widest px-2.5 py-1 ds-border border-ds-bone bg-ds-bone text-ds-ink">
            {COPY.output_vibe}: {result.vibe}
          </span>
        )}
      </div>

      {result.psychological_framework && (
        <div className="mb-5 pl-3 border-l-2 border-ds-flame" data-testid={TID.outputFramework}>
          <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-flame mb-1.5">
            {COPY.output_framework}
          </div>
          <div className="text-base leading-snug text-ds-bone/90">
            {result.psychological_framework}
          </div>
        </div>
      )}

      {result.not_about_you && (
        <div className="mb-5 pl-3 border-l-2 border-emerald-400" data-testid={TID.outputNotAboutYou}>
          <div className="ds-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-1.5">
            {COPY.output_not_about_you}
          </div>
          <div className="text-base leading-snug text-ds-bone/95">
            {result.not_about_you}
          </div>
        </div>
      )}

      {result.actionable && (
        <div className="mb-5 ds-border border-ds-yellow p-3 bg-ds-oxblood" data-testid={TID.outputActionable}>
          <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-yellow mb-1">
            {COPY.output_actionable}
          </div>
          <div className="text-base leading-snug text-ds-bone">
            {result.actionable}
          </div>
        </div>
      )}

      {result.suggested_reply && (
        <div className="mb-5 ds-border border-ds-flame p-3 bg-ds-oxblood">
          <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-flame mb-1">
            {COPY.output_reply}
          </div>
          <div data-testid={TID.outputReply} className="text-base leading-snug">
            {result.suggested_reply}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {result.red_flags?.length > 0 && (
          <div data-testid={TID.outputRedFlags}>
            <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-flame mb-1.5">
              {COPY.output_red}
            </div>
            <ul className="space-y-1">
              {result.red_flags.map((f, i) => (
                <li key={i} className="text-sm text-ds-bone/90 flex gap-2">
                  <span className="text-ds-flame">▸</span>{f}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result.green_flags?.length > 0 && (
          <div data-testid={TID.outputGreenFlags}>
            <div className="ds-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-1.5">
              {COPY.output_green}
            </div>
            <ul className="space-y-1">
              {result.green_flags.map((f, i) => (
                <li key={i} className="text-sm text-ds-bone/90 flex gap-2">
                  <span className="text-emerald-400">▸</span>{f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
